import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SearchableSelect from '../../../components/SearchableSelect';
import FlagIcon from '../../../components/FlagIcon';
import { PARSE_DOCUMENT_ACCEPT } from '../../../utils/documentUploadHelpers';
import { useNumberFormat } from '../../../hooks/useNumberFormat';

function formatAmountDisplay(amount, formatNumber) {
  if (amount === null || amount === undefined || amount === '') return formatNumber(0);
  if (typeof amount === 'string' && amount.trim()) return amount.trim();
  if (Number.isNaN(Number(amount))) return formatNumber(0);
  return formatNumber(Number(amount));
}

function AmountSummaryCard({ receiveCurrency, sendCurrency, receiveAmount, sendAmount, onEdit }) {
  const { formatNumber } = useNumberFormat();
  return (
    <div className='relative overflow-hidden rounded-[14px] bg-[var(--n900)] p-7 text-white shadow-[var(--shadow-md)]'>
      <div
        className='pointer-events-none absolute inset-0'
        style={{ background: 'radial-gradient(circle at 100% 0%, rgba(255,255,255,0.05), transparent 60%)' }}
        aria-hidden='true'
      />
      <div className='relative'>
        <div className='mb-2.5 flex items-center justify-between gap-3'>
          <div className='inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/55 font-[family-name:var(--font-ui)]'>
            <span>Recipient gets</span>
            <span
              className='flex h-5 w-5 shrink-0 items-center justify-center rounded-[8px] border border-white/20 bg-white/10'
              aria-hidden='true'
            >
              <FlagIcon currencyCode={receiveCurrency} className='h-2.5 w-3.5 rounded-sm' />
            </span>
          </div>
          <button
            type='button'
            title='Edit amount'
            onClick={onEdit}
            className='inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-white font-[family-name:var(--font-ui)] underline decoration-white/35 underline-offset-[3px] transition-opacity hover:opacity-80'
          >
            <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z' />
            </svg>
            <span>Edit</span>
          </button>
        </div>
        <div className='mb-3.5 flex items-baseline gap-3.5 font-display text-[56px] font-light leading-none tracking-[-0.025em]'>
          <span>{formatAmountDisplay(receiveAmount, formatNumber)}</span>
          <span className='text-[22px] font-normal tracking-[0.04em] text-white/65'>{receiveCurrency}</span>
        </div>
        <div className='my-4 h-px bg-white/10' />
        <div className='flex items-baseline justify-between'>
          <span className='text-[13px] text-white/55 font-[family-name:var(--font-ui)]'>You send</span>
          <span className='font-display text-[22px] font-normal tracking-[-0.01em]'>
            {formatAmountDisplay(sendAmount, formatNumber)}{' '}
            <span className='text-sm tracking-[0.05em] text-white/55'>{sendCurrency}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

AmountSummaryCard.propTypes = {
  receiveCurrency: PropTypes.string.isRequired,
  sendCurrency: PropTypes.string.isRequired,
  receiveAmount: PropTypes.string,
  sendAmount: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
};

const AMOUNT_INPUT_CLASS =
  'w-full rounded-xl border-[1.5px] border-[var(--color-border)] bg-[var(--off)] py-[18px] pl-5 pr-[156px] text-2xl font-medium tabular-nums text-[var(--n900)] outline-none transition-[border-color,background] focus:border-[var(--n600)] focus:bg-[var(--white)] font-[family-name:var(--font-ui)]';

const FIELD_LABEL_CLASS =
  'mb-2 block text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]';

function CurrencyFlagBox({ currencyCode }) {
  return (
    <span className='flex h-8 w-8 shrink-0 items-center justify-center' aria-hidden='true'>
      <FlagIcon currencyCode={currencyCode} className='h-4 w-5 rounded-sm' />
    </span>
  );
}

CurrencyFlagBox.propTypes = {
  currencyCode: PropTypes.string.isRequired,
};

const currencyBadgeWrapClass =
  'absolute right-5 top-1/2 flex -translate-y-1/2 items-center gap-2 font-[family-name:var(--font-ui)]';

function CurrencyBadge({ code, currencies, onChange }) {
  const hasChoice = currencies.length > 1;

  if (hasChoice) {
    return (
      <div className={currencyBadgeWrapClass}>
        <CurrencyFlagBox currencyCode={code} />
        <select
          value={code}
          onChange={(e) => onChange(e.target.value)}
          className='max-w-[72px] cursor-pointer appearance-none border-0 bg-transparent py-0 pr-1 text-sm font-medium tracking-[0.03em] text-[var(--n800)] outline-none'
          aria-label='Currency'
        >
          {currencies.map((currency) => (
            <option key={currency.code} value={currency.code}>
              {currency.code}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={`${currencyBadgeWrapClass} pointer-events-none`}>
      <CurrencyFlagBox currencyCode={code} />
      <span className='text-sm font-medium tracking-[0.03em] text-[var(--n800)]'>{code}</span>
    </div>
  );
}

CurrencyBadge.propTypes = {
  code: PropTypes.string.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
};

function SendMoneyAmountStep({
  sendCurrency,
  receiveCurrency,
  sendAmount,
  receiveAmount,
  sendCurrencies,
  receiveCurrencies,
  onSendCurrencyChange,
  onSendAmountChange,
  onReceiveAmountChange,
  spotRate,
  gettingSpotRate,
  showFxBar,
  balances,
  sendAmountNum,
  formatCurrency,
  isSwiftTransfer,
  parsingDocument,
  parsingError,
  transactionDocumentFileName,
  transactionDocumentType,
  transactionDocumentId,
  onDocumentTypeChange,
  onDocumentIdChange,
  onFileInputChange,
  onDragOver,
  onDrop,
  renderUploadContent,
  paymentReference,
  onPaymentReferenceChange,
  isPaymentReferenceValid,
  paymentPurpose,
  onPaymentPurposeChange,
  paymentPurposeOptions,
  onContinue,
  gettingCrossBorderQuote,
  canContinue,
  startInEditMode = false,
}) {
  const { formatNumber } = useNumberFormat();
  const hasAmountValues = Boolean(sendAmount?.trim() || receiveAmount?.trim());
  const [isEditingAmount, setIsEditingAmount] = useState(!hasAmountValues || startInEditMode);
  const receiveAmountInputRef = useRef(null);

  useEffect(() => {
    if (startInEditMode) {
      setIsEditingAmount(true);
    }
  }, [startInEditMode]);

  useEffect(() => {
    if (!sendAmount?.trim() && !receiveAmount?.trim()) {
      setIsEditingAmount(true);
    }
  }, [sendAmount, receiveAmount]);

  useEffect(() => {
    if (!isEditingAmount) return undefined;
    const timer = window.setTimeout(() => {
      receiveAmountInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isEditingAmount]);

  const handleEditAmount = () => {
    setIsEditingAmount(true);
  };

  const renderDocumentUploadZone = (inputId) => (
    <div className='space-y-2'>
      <label htmlFor={inputId} className={FIELD_LABEL_CLASS}>
        Upload document
      </label>
      <div onDragOver={onDragOver} onDrop={onDrop} className='relative'>
        <input
          type='file'
          id={inputId}
          accept={PARSE_DOCUMENT_ACCEPT}
          onChange={onFileInputChange}
          className='absolute inset-0 h-full w-full cursor-pointer opacity-0'
          disabled={parsingDocument}
        />
        <div className='flex min-h-[128px] flex-col items-center justify-center rounded-lg border-2 border-dashed border-[var(--color-border)] bg-[var(--off)] transition-colors hover:bg-[var(--white)]'>
          {renderUploadContent()}
        </div>
      </div>
      {!isSwiftTransfer && (
        <p className='text-xs leading-relaxed text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
          Upload an invoice or transaction document. AI will extract and pre-fill amount, description, and related fields where possible.
        </p>
      )}
      {parsingError && (
        <div className='rounded-lg border border-[var(--color-error,#c4391c)]/25 bg-[var(--color-error,#c4391c)]/5 px-3 py-2'>
          <p className='m-0 text-xs text-[var(--color-error,#c4391c)] font-[family-name:var(--font-ui)]'>{parsingError}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className='grid gap-[18px]'>
      {!isEditingAmount && hasAmountValues ? (
        <AmountSummaryCard
          receiveCurrency={receiveCurrency}
          sendCurrency={sendCurrency}
          receiveAmount={receiveAmount}
          sendAmount={sendAmount}
          onEdit={handleEditAmount}
        />
      ) : (
        <div className='rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-7 shadow-[var(--shadow-md)]'>
        {showFxBar && (
          <div className='mb-[22px] flex items-center justify-between rounded-[10px] border border-[var(--n900)] bg-[var(--n900)] px-4 py-3'>
            <div className='flex items-center gap-2.5'>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l4-4M17 20l-4-4' />
              </svg>
              <span className='text-[13px] font-medium tabular-nums text-white font-[family-name:var(--font-ui)]'>
                {gettingSpotRate ? 'Loading rate…' : `1 ${sendCurrency} = ${spotRate != null ? formatNumber(spotRate, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : '—'} ${receiveCurrency}`}
              </span>
              <span className='rounded border border-white/20 px-[7px] py-[3px] text-[9.5px] font-semibold uppercase tracking-[0.14em] text-white/55'>
                Indicative
              </span>
            </div>
            <span className='inline-flex items-center gap-[7px] text-[11.5px] font-medium text-white/75 font-[family-name:var(--font-ui)]'>
              <span className='h-[7px] w-[7px] rounded-full bg-[#22c55e]' aria-hidden='true' />
              Live FX rate
            </span>
          </div>
        )}

        <div className='mb-[18px]'>
          <label htmlFor='send-amount' className={FIELD_LABEL_CLASS}>
            You send
          </label>
          <div className='relative'>
            <input
              type='text'
              inputMode='decimal'
              id='send-amount'
              placeholder='0,00'
              value={sendAmount}
              onChange={(e) => onSendAmountChange(e.target.value)}
              className={AMOUNT_INPUT_CLASS}
            />
            <CurrencyBadge code={sendCurrency} currencies={sendCurrencies} onChange={onSendCurrencyChange} />
          </div>
          <div className='mt-2 flex justify-between text-xs text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
            <span>Current balance</span>
            <span className={sendAmountNum > balances?.[sendCurrency] ? 'text-[var(--color-error,#c4391c)]' : ''}>
              {formatCurrency(balances?.[sendCurrency], sendCurrency)}
            </span>
          </div>
        </div>

        <div>
          <label htmlFor='receive-amount' className={FIELD_LABEL_CLASS}>
            Recipient gets
          </label>
          <div className='relative'>
            <input
              ref={receiveAmountInputRef}
              type='text'
              inputMode='decimal'
              id='receive-amount'
              placeholder='0,00'
              value={receiveAmount}
              onChange={(e) => onReceiveAmountChange(e.target.value)}
              className={AMOUNT_INPUT_CLASS}
            />
            <CurrencyBadge code={receiveCurrency} currencies={receiveCurrencies} onChange={() => {}} />
          </div>
        </div>
        </div>
      )}

      {isSwiftTransfer && (
        <div className='rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-6 shadow-[var(--shadow-md)]'>
          <h4 className='m-0 mb-4 text-sm font-semibold text-[var(--n900)] font-[family-name:var(--font-ui)]'>Swift document *</h4>
          <div className='space-y-4'>
            {renderDocumentUploadZone('transaction-swift-document-upload')}
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div>
                <label htmlFor='documentType' className={FIELD_LABEL_CLASS}>
                  Document type *
                </label>
                <select
                  id='documentType'
                  value={transactionDocumentType}
                  onChange={(e) => onDocumentTypeChange(e.target.value)}
                  disabled={!transactionDocumentFileName}
                  className='h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3 text-sm text-[var(--n900)] disabled:cursor-not-allowed disabled:opacity-50'
                >
                  <option value=''>Select type</option>
                  <option value='invoice'>Invoice</option>
                  <option value='purchase_order'>Purchase Order</option>
                  <option value='delivery_slip'>Delivery Slip</option>
                  <option value='contract'>Contract</option>
                  <option value='customs_declaration'>Customs Declaration</option>
                  <option value='bill_of_lading'>Bill of Lading</option>
                  <option value='others'>Others</option>
                </select>
              </div>
              <div>
                <label htmlFor='documentId' className={FIELD_LABEL_CLASS}>
                  Id number/code *
                </label>
                <input
                  type='text'
                  id='documentId'
                  placeholder='Document ID'
                  value={transactionDocumentId}
                  onChange={(e) => onDocumentIdChange(e.target.value)}
                  disabled={!transactionDocumentFileName}
                  className='h-10 w-full rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3 text-sm disabled:cursor-not-allowed disabled:opacity-50'
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-6 shadow-[var(--shadow-md)]'>
        <div>
          <label htmlFor='paymentReference' className='mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
            Payment reference *
          </label>
          <textarea
            id='paymentReference'
            rows={2}
            placeholder='e.g. Invoice #2024-0587 — Q4 services'
            value={paymentReference}
            onChange={(e) => onPaymentReferenceChange(e.target.value)}
            className={`min-h-[56px] w-full resize-y rounded-lg border bg-[var(--white)] px-3.5 py-3 text-sm text-[var(--n900)] outline-none font-[family-name:var(--font-ui)] ${
              paymentReference.trim() && !isPaymentReferenceValid(paymentReference)
                ? 'border-[var(--color-error,#c4391c)]'
                : 'border-[var(--color-border)]'
            }`}
          />
          {paymentReference.trim() && !isPaymentReferenceValid(paymentReference) && (
            <p className='mt-2 text-xs text-[var(--color-error,#c4391c)] font-[family-name:var(--font-ui)]'>
              Only letters, numbers, spaces, dashes, and colons are allowed.
            </p>
          )}
          <p className='mt-2 text-[11.5px] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
            Shown to the recipient on their bank statement where the local rail supports it.
          </p>
        </div>

        <div className='mt-5'>
          <label htmlFor='paymentPurpose' className='mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
            Payment purpose *
          </label>
          <SearchableSelect
            id='paymentPurpose'
            name='paymentPurpose'
            value={paymentPurpose}
            onChange={(e) => onPaymentPurposeChange(e.target.value)}
            placeholder='Select payment purpose'
            options={paymentPurposeOptions}
            hideValue
            className='w-full'
            menuMinWidth='w-full'
            menuPlacement='auto'
            searchPlaceholder='Search payment purpose'
            menuMaxHeight={320}
            triggerClassName='flex h-11 w-full items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-3.5 py-2 text-sm text-[var(--n900)] outline-none font-[family-name:var(--font-ui)] focus-visible:ring-2 focus-visible:ring-[var(--n600)] focus-visible:ring-offset-0'
          />
        </div>
      </div>

      <button
        type='button'
        onClick={onContinue}
        disabled={!canContinue || gettingCrossBorderQuote}
        className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-[22px] py-[13px] text-sm font-medium transition-[background,color,box-shadow] font-[family-name:var(--font-ui)] ${
          canContinue && !gettingCrossBorderQuote
            ? 'border border-[var(--n800)] bg-[var(--n800)] text-white shadow-[var(--shadow-cta)] hover:opacity-90'
            : 'cursor-not-allowed border border-[var(--color-border)] bg-transparent text-[#7a99b5] shadow-none'
        }`}
      >
        {gettingCrossBorderQuote ? 'Generating quote…' : 'Continue'}
        {!gettingCrossBorderQuote && (
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M5 12h14M13 5l7 7-7 7' />
          </svg>
        )}
      </button>
    </div>
  );
}

SendMoneyAmountStep.propTypes = {
  sendCurrency: PropTypes.string.isRequired,
  receiveCurrency: PropTypes.string.isRequired,
  sendAmount: PropTypes.string.isRequired,
  receiveAmount: PropTypes.string.isRequired,
  sendCurrencies: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })).isRequired,
  receiveCurrencies: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })).isRequired,
  onSendCurrencyChange: PropTypes.func.isRequired,
  onSendAmountChange: PropTypes.func.isRequired,
  onReceiveAmountChange: PropTypes.func.isRequired,
  spotRate: PropTypes.number,
  gettingSpotRate: PropTypes.bool,
  showFxBar: PropTypes.bool,
  balances: PropTypes.objectOf(PropTypes.number),
  sendAmountNum: PropTypes.number,
  formatCurrency: PropTypes.func.isRequired,
  isSwiftTransfer: PropTypes.bool,
  parsingDocument: PropTypes.bool,
  parsingError: PropTypes.string,
  transactionDocumentFileName: PropTypes.string,
  transactionDocumentType: PropTypes.string,
  transactionDocumentId: PropTypes.string,
  onDocumentTypeChange: PropTypes.func.isRequired,
  onDocumentIdChange: PropTypes.func.isRequired,
  onFileInputChange: PropTypes.func.isRequired,
  onDragOver: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  renderUploadContent: PropTypes.func.isRequired,
  paymentReference: PropTypes.string.isRequired,
  onPaymentReferenceChange: PropTypes.func.isRequired,
  isPaymentReferenceValid: PropTypes.func.isRequired,
  paymentPurpose: PropTypes.string.isRequired,
  onPaymentPurposeChange: PropTypes.func.isRequired,
  paymentPurposeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  onContinue: PropTypes.func.isRequired,
  gettingCrossBorderQuote: PropTypes.bool,
  canContinue: PropTypes.bool.isRequired,
  startInEditMode: PropTypes.bool,
};

SendMoneyAmountStep.defaultProps = {
  isSwiftTransfer: false,
  startInEditMode: false,
};

export default SendMoneyAmountStep;
